import React, { useEffect, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { MdAdd, MdFolderOpen } from 'react-icons/md';
import { StatCards } from '_atoms';
import { setSelectedArchive, setArchives, selectArchives } from '_redux/slices/archive/archive';
import { selectFirebaseInfo } from '_redux/slices/main-screen/user';
import { subscribeToArchives } from '_services/archive-service';
import { Colors } from '_styles';

const containerStyle = {
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: '600px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    marginTop: 16,
};

const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    fontSize: '14px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
};

const headerStyle = {
    backgroundColor: '#f8fafc',
    color: '#374151',
    fontWeight: '600',
    padding: '12px 16px',
    textAlign: 'left',
    borderBottom: '2px solid #e5e7eb',
    borderRight: '1px solid #e5e7eb',
    fontSize: '13px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    position: 'sticky',
    top: 0,
    zIndex: 2,
};

const cellStyle = {
    padding: '12px 16px',
    borderBottom: '1px solid #f3f4f6',
    borderTop: '1px solid #f3f4f6',
    color: '#374151',
    fontSize: '14px',
};

const emptyStateStyle = {
    textAlign: 'center',
    padding: '56px 20px',
    color: '#6b7280',
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
};

const emptyIconWrap = {
    width: 64,
    height: 64,
    borderRadius: '50%',
    background: '#eef5fd',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
};

const addReportButton = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 20,
    padding: '10px 22px',
    border: 'none',
    borderRadius: 8,
    backgroundColor: Colors.PRM_LIGHT_BLUE,
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(48, 127, 226, 0.3)',
};

const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString();
};

const formatDateTime = (timestamp) => {
    if (!timestamp) return '—';
    const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString();
};

const HEADERS = ['Flight', 'Route', 'Flight Date', 'Files', 'Created By', 'Created At'];

const ArchiveList = ({ onSelect, onAddArchive }) => {
    const dispatch = useDispatch();
    const dbUser = useSelector(selectFirebaseInfo);
    const archives = useSelector(selectArchives);
    const [hoveredRow, setHoveredRow] = useState(null);

    const iata = dbUser?.details?.authority;

    useEffect(() => {
        if (!iata) return;
        const unsubscribe = subscribeToArchives(iata, (list) => dispatch(setArchives(list)));
        return () => unsubscribe();
    }, [iata, dispatch]);

    const handleRowClick = (archive) => {
        dispatch(setSelectedArchive(archive));
        onSelect?.(archive);
    };

    const totalArchives = archives.length;
    const totalAttachments = archives.reduce((sum, a) => sum + (a.attachments?.length ?? 0), 0);
    const archivesWithFiles = archives.filter((a) => (a.attachments?.length ?? 0) > 0).length;

    const statCards = [
        {
            title: 'Total Archives',
            value: totalArchives,
            subtitle: 'Flight archives on record',
            color: '#3b82f6',
        },
        {
            title: 'Total Documents',
            value: totalAttachments,
            subtitle: 'Attachments across all archives',
            color: '#10b981',
        },
        {
            title: 'Archives with Files',
            value: archivesWithFiles,
            subtitle: `${totalArchives - archivesWithFiles} empty archive${totalArchives - archivesWithFiles === 1 ? '' : 's'}`,
            color: archivesWithFiles === totalArchives && totalArchives > 0 ? '#10b981' : '#f59e0b',
        },
    ];

    return (
        <>
            <Row style={{ marginTop: 20, marginBottom: 20 }}>
                <Col>
                    <StatCards cards={statCards} />
                </Col>
            </Row>

            <Row>
                <Col>
                    {archives.length === 0 ? (
                        <div style={emptyStateStyle}>
                            <div style={emptyIconWrap}>
                                <MdFolderOpen size={32} color={Colors.PRM_LIGHT_BLUE} />
                            </div>
                            <h3 style={{ fontSize: 18, fontWeight: 600, color: '#374151', margin: 0 }}>
                                No Archives Yet
                            </h3>
                            <p style={{ margin: '6px 0 0', fontSize: 14 }}>
                                No flight archives have been created for this airport. Use the Create Archive tab to add one.
                            </p>
                            {onAddArchive && (
                                <button type="button" onClick={onAddArchive} style={addReportButton}>
                                    <MdAdd size={18} />
                                    Add Archive
                                </button>
                            )}
                        </div>
                    ) : (
                        <div style={containerStyle}>
                            <table style={tableStyle}>
                                <thead>
                                    <tr>
                                        {HEADERS.map((h) => (
                                            <th key={h} style={headerStyle}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {archives.map((archive, idx) => {
                                        const attachmentCount = archive.attachments?.length ?? 0;
                                        const isHovered = hoveredRow === idx;
                                        const creatorName = archive.createdBy?.name ?? '—';
                                        return (
                                            <tr
                                                key={archive.id}
                                                style={{
                                                    backgroundColor: isHovered ? '#f9fafb' : 'white',
                                                    cursor: 'pointer',
                                                    transition: 'background-color 0.1s ease',
                                                }}
                                                onMouseEnter={() => setHoveredRow(idx)}
                                                onMouseLeave={() => setHoveredRow(null)}
                                                onClick={() => handleRowClick(archive)}
                                            >
                                                <td style={{ ...cellStyle, fontWeight: 600 }}>
                                                    ✈ {archive.flightNumber}
                                                </td>
                                                <td style={cellStyle}>
                                                    {archive.origin} → {archive.destination}
                                                </td>
                                                <td style={cellStyle}>{formatDate(archive.flightDate)}</td>
                                                <td style={cellStyle}>
                                                    <span style={{
                                                        background: attachmentCount > 0 ? '#DBEAFE' : '#F3F4F6',
                                                        color: attachmentCount > 0 ? '#1E40AF' : '#6B7280',
                                                        padding: '2px 10px',
                                                        borderRadius: 4,
                                                        fontSize: 12,
                                                        fontWeight: 600,
                                                    }}>
                                                        {attachmentCount} file{attachmentCount === 1 ? '' : 's'}
                                                    </span>
                                                </td>
                                                <td style={cellStyle}>{creatorName}</td>
                                                <td style={{ ...cellStyle, color: '#6b7280' }}>
                                                    {formatDateTime(archive.createdAt)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Col>
            </Row>
        </>
    );
};

export default ArchiveList;
